/**
 * `<caos-avatar>` — a person drawn as a circle: their picture, else their initials, else a glyph.
 * (Catalog: Data display › Avatar — a person drawn as a circle: their picture, else their initials, else a glyph.)
 *
 * CAOS-594. Record headers, open-record strips, owner fields and people pickers all want one, and the
 * fallback is the fiddly part, so it is solved once here:
 *
 *   1. `src` is set and the picture loads  → the picture, cropped to the circle.
 *   2. no `src`, or the picture fails      → initials from `name`: the first letter of the first word
 *                                            and of the last word.
 *   3. no usable name either               → the `user` glyph from the one vocabulary.
 *
 * A picture that failed is not tried again until `src` changes, so a broken link never flickers
 * between the picture and the initials.
 *
 * SIZE comes from the platform scale (`src/sizes.ts`): the diameter is `--caos-size-<step>-height`, and
 * the initials scale with it. When `size` is absent or names no step, it is `md`. There is no
 * avatar-named token.
 *
 * NAME for a screen reader: `alt`, else `name`, as one image. The picture and the initials inside it
 * are hidden, so the name is heard once. With neither, the avatar is decorative.
 *
 * Styling seams: `::part(avatar)`, `::part(image)`, `::part(initials)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { sizeRules, type SizeStep } from '../sizes.js';
import { makeIcon } from './icons.js';

export const AVATAR_DEFAULT_SIZE: SizeStep = 'md';

/** `Morgan Lee` → `ML`, `Cher` → `C`, blank → ''. The first letter of the first and of the last word. */
export function initialsOf(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = Array.from(words[0]!)[0] ?? '';
  const last = words.length > 1 ? (Array.from(words[words.length - 1]!)[0] ?? '') : '';
  return (first + last).toLocaleUpperCase();
}

/**
 * The avatar's own styles, as written. Exported because the initials' `calc(var(...) * 0.4)` is valid
 * CSS that a browser applies but the test DOM's parser drops, so the rule can only be read here.
 */
export const AVATAR_STYLES = `
  :host { display: inline-flex; vertical-align: middle; flex: none; }
  .avatar {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: var(--caos-radius-pill);
    overflow: hidden;
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
  }
  .image { display: block; width: 100%; height: 100%; object-fit: cover; }
  .initials {
    font-family: var(--caos-font-mono);
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.04em;
    line-height: 1;
    user-select: none;
  }
  .glyph { display: block; width: 60%; height: 60%; }
  ${sizeRules(
    AVATAR_DEFAULT_SIZE,
    (host, length) => `${host} { width: ${length}; height: ${length}; }\n  ${host} .initials { font-size: calc(${length} * 0.4); }`,
  )}
`;

const sheet = makeSheet(AVATAR_STYLES);

export class CaosAvatar extends CaosElement {
  static get observedAttributes(): string[] {
    return ['src', 'name', 'alt'];
  }

  private readonly frame: HTMLSpanElement;
  /** The address whose picture failed, so it is not tried again until `src` changes. */
  private failedSrc: string | null = null;

  constructor() {
    super(sheet);
    this.frame = document.createElement('span');
    this.frame.className = 'avatar';
    this.frame.setAttribute('part', 'avatar');
    this.root.append(this.frame);
    this.render();
  }

  attributeChangedCallback(attribute: string, oldValue: string | null, newValue: string | null): void {
    if (attribute === 'src' && oldValue !== newValue) this.failedSrc = null;
    this.render();
  }

  private render(): void {
    const src = (this.getAttribute('src') ?? '').trim();
    const name = (this.getAttribute('name') ?? '').trim();
    const initials = initialsOf(name);

    if (src && src !== this.failedSrc) {
      const current = this.frame.firstElementChild;
      // The same picture already drawn is kept, so a change to the name does not reload it.
      if (!(current instanceof HTMLImageElement && current.getAttribute('src') === src)) {
        const img = document.createElement('img');
        img.className = 'image';
        img.setAttribute('part', 'image');
        img.alt = '';
        img.addEventListener(
          'error',
          () => {
            this.failedSrc = src;
            this.render();
          },
          { once: true },
        );
        img.setAttribute('src', src);
        this.frame.replaceChildren(img);
      }
    } else if (initials) {
      const text = document.createElement('span');
      text.className = 'initials';
      text.setAttribute('part', 'initials');
      text.setAttribute('aria-hidden', 'true');
      text.textContent = initials;
      this.frame.replaceChildren(text);
    } else {
      const glyph = makeIcon('user');
      glyph.classList.add('glyph');
      glyph.setAttribute('aria-hidden', 'true');
      this.frame.replaceChildren(glyph);
    }

    const label = (this.getAttribute('alt') ?? '').trim() || name;
    if (label) {
      this.frame.setAttribute('role', 'img');
      this.frame.setAttribute('aria-label', label);
      this.frame.removeAttribute('aria-hidden');
    } else {
      this.frame.removeAttribute('role');
      this.frame.removeAttribute('aria-label');
      this.frame.setAttribute('aria-hidden', 'true');
    }
  }
}

define('caos-avatar', CaosAvatar);

declare global {
  interface HTMLElementTagNameMap {
    'caos-avatar': CaosAvatar;
  }
}
